import React, { useRef, useState, useEffect } from 'react';
import { Loader } from 'common/lazy';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { useClassName } from 'common/hooks';
import { CustomMessage } from '../CustomMessage';
import { SVGIcon as Icon } from 'common/components';
import { ModalCalendar } from '../ModalCalendar';
import {
  CALENDAR_DATE_STATUS,
  getCalendarPriceLabel,
} from 'utils/calendarPricing';
import './horizontal-date-picker.less';

const getInitialDate = (startDate) => {
  const today = DateTime.local().startOf('day');
  if (!startDate) return today;

  const initialDate = DateTime.fromISO(startDate);
  if (!initialDate.isValid) return today;
  if (initialDate < today) return today;

  return initialDate;
};

const HorizontalDatePicker = ({
  startDate,
  days,
  selectedDate,
  footerMessages,
  hiddenControls,
  onDateSelected,
  isDateUnavailable,
  getDateStatus,
  isTicketsCalendar,
  priceTypes,
  regularPriceTypes,
  onDateSelectedError,
  selectOnLoad,
  cardVariant,
  datePricing,
  title,
  legend,
  modalLegendTypes,
  modalVenueName,
  modalVenueId,
  modalRidesCards,
  modalOpeningHours,
  modalMaxDate,
}) => {
  const className = useClassName('HorizontalDatePicker');
  const datesContainerRef = useRef(null);
  const initialDate = getInitialDate(startDate);
  const [open, setOpen] = useState(false);
  const [loadingDate, setLoadingDate] = useState(null);
  const [datesRange, setDatesRange] = useState([]);

  useEffect(() => {
    const dates = Array.from({ length: days }, (v, k) =>
      initialDate.plus({ days: k }).startOf('day')
    );
    setDatesRange(dates);

    if (startDate && selectOnLoad) {
      selectDate(initialDate.toISODate());
    }
  }, []);

  const toISOString = (date) => date.toFormat('yyyy-MM-dd');

  const isSelected = (date) => toISOString(date) === selectedDate;

  const selectDate = (date, { allowCurrent = false } = {}) => {
    const isCurrentDate = date === selectedDate;
    if (isDateUnavailable(date) && !(allowCurrent && isCurrentDate)) return;

    if (onDateSelected.constructor.name === 'AsyncFunction') {
      setLoadingDate(date);
      onDateSelected(date)
        .catch((error) => {
          onDateSelectedError(date, error);
        })
        .finally(() => setLoadingDate(null));
    } else {
      onDateSelected(date);
    }
  };

  const updateSelectedDate = (calendarSelectedDate) => {
    setOpen(false);

    if (!calendarSelectedDate) return;

    const newDate = DateTime.fromJSDate(calendarSelectedDate);
    setDatesRange(
      Array.from({ length: days }, (v, k) => newDate.plus({ days: k }))
    );

    selectDate(toISOString(newDate));
  };

  const closeModal = () => setOpen(false);

  const dragState = useRef({
    down: false,
    startX: 0,
    scrollLeft: 0,
    moved: false,
  });

  const onDragStart = (e) => {
    const el = datesContainerRef.current;
    if (!el) return;
    dragState.current = {
      down: true,
      startX: e.pageX - el.offsetLeft,
      scrollLeft: el.scrollLeft,
      moved: false,
    };
  };

  const onDragMove = (e) => {
    const el = datesContainerRef.current;
    if (!el || !dragState.current.down) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - dragState.current.startX;
    if (Math.abs(walk) > 5) dragState.current.moved = true;
    el.scrollLeft = dragState.current.scrollLeft - walk;
  };

  const onDragEnd = () => {
    dragState.current.down = false;
  };

  const findPriceType = (date) => {
    const override = priceTypes.find((priceType) => date === priceType.date);

    if (override) {
      return ['price-type', override.type];
    }

    const weekday = DateTime.fromISO(date).weekdayLong.toLowerCase();

    const regularPriceType = regularPriceTypes?.[weekday];

    return regularPriceType
      ? ['price-type', regularPriceType]
      : ['regular-date'];
  };

  const renderCardVariantDay = (date) => {
    const iso = date.toISODate();
    const pricing = datePricing?.[iso];
    const priceLabel = getCalendarPriceLabel(pricing);
    const status = getDateStatus(iso);
    const closed = status === CALENDAR_DATE_STATUS.CLOSED;
    const soldOut = status === CALENDAR_DATE_STATUS.SOLD_OUT;
    const unavailable = isDateUnavailable(toISOString(date));
    const statusLabel = closed ? 'CLOSED' : soldOut ? 'SOLD OUT' : null;
    // While the tapped date resolves, keep the card visible (just dimmed) and
    // non-clickable instead of swapping in a spinner.
    const isLoading = loadingDate === iso;
    // Focus (selected highlight) applies on click, not after the async callback.
    // While a date is loading, ONLY that date reads as selected — the previously
    // selected date must not stay highlighted (selectedDate updates later, in the
    // callback). Otherwise fall back to the real selection.
    const selected = loadingDate ? isLoading : isSelected(date);
    const handleCardClick = () => {
      if (isLoading) return;
      if (dragState.current.moved) return;
      if (unavailable) return;
      if (selected) {
        onDateSelected(iso);
        return;
      }
      selectDate(iso);
    };

    return (
      <div
        className={`${className(['day', 'card'])} ${
          selected ? 'selected' : ''
        } ${soldOut ? className('sold-out') : ''} ${
          closed ? className('closed') : ''
        } ${unavailable ? className('unavailable') : ''} ${
          isLoading ? className('loading') : ''
        }`}
        onClick={handleCardClick}>
        <p className="week-day">{date.toFormat('EEE')}</p>
        <p className="day-number">{date.toFormat('d MMM')}</p>
        <p className="price">{unavailable ? ' ' : priceLabel || ' '}</p>
        {statusLabel && (
          <span className={className('status-badge')}>{statusLabel}</span>
        )}
        {!unavailable && pricing?.color && (
          <span
            className={className('tier-dot')}
            style={{ backgroundColor: pricing.color }}
          />
        )}
      </div>
    );
  };

  const renderDates = () => {
    const dragProps = cardVariant
      ? {
          onMouseDown: onDragStart,
          onMouseMove: onDragMove,
          onMouseUp: onDragEnd,
          onMouseLeave: onDragEnd,
        }
      : {};

    return (
      <div
        ref={datesContainerRef}
        className={`${className('dates-container')} ${
          cardVariant ? className('dates-scroll') : ''
        }`}
        {...dragProps}>
        {datesRange.map((date) => (
          <div key={date.toISO()} className={className('day-container')}>
            {cardVariant ? (
              renderCardVariantDay(date)
            ) : loadingDate === toISOString(date) ? (
              <div
                className={className([
                  'day',
                  ...findPriceType(date.toISODate()),
                ])}>
                <Loader active inline size="tiny" />
              </div>
            ) : (
              <div
                className={`
                    ${className('day')} ${isSelected(date) ? 'selected' : ''}
                    ${
                      isDateUnavailable(toISOString(date))
                        ? className('unavailable')
                        : className(findPriceType(date.toISODate()))
                    }`}
                onClick={() => selectDate(toISOString(date))}>
                <p className="week-day">{date.toFormat('EEE')}</p>
                <p className="day-number">{date.toFormat('d')}</p>
                <p className="month">{date.toFormat('MMM')}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const isPrevDisabled = () => {
    if (!datesRange.length) return true;
    const today = DateTime.local().startOf('day');
    if (!today.diff(datesRange[0], 'days').days) return true;
    return false;
  };

  const renderPrev = () => {
    return (
      <div
        className={className(['prev', isPrevDisabled() ? 'prev-disabled' : ''])}
        onClick={() => {
          if (isPrevDisabled()) return;
          setDatesRange(
            [datesRange[0].plus({ days: -1 }), ...datesRange].slice(0, days)
          );
        }}>
        <Icon
          name={`chevron-left-small${isPrevDisabled() ? '-disabled' : ''}`}
        />
      </div>
    );
  };

  const renderNext = () => {
    return (
      <div
        className={className('next')}
        onClick={() =>
          setDatesRange(
            [
              ...datesRange,
              datesRange[datesRange.length - 1].plus({ days: 1 }),
            ].slice(1)
          )
        }>
        <Icon name="chevron-right-small" />
      </div>
    );
  };

  return (
    <div className={className('container')}>
      <div
        className={className(['header', cardVariant ? 'header-redesign' : ''])}>
        <h2>{title}</h2>
        <p className="link" onClick={() => setOpen(true)}>
          Open calendar
        </p>
        <ModalCalendar
          isDateUnavailable={isDateUnavailable}
          getDateStatus={getDateStatus}
          initialDate={selectedDate}
          open={open}
          updateSelectedDate={updateSelectedDate}
          closeModal={closeModal}
          priceTypes={priceTypes}
          regularPriceTypes={regularPriceTypes}
          isTicketsCalendar={isTicketsCalendar}
          datePricing={datePricing}
          legendTypes={modalLegendTypes}
          venueName={modalVenueName}
          venueId={modalVenueId}
          ridesCards={modalRidesCards}
          openingHours={modalOpeningHours}
          maxDate={modalMaxDate}
        />
      </div>
      {legend && <div className={className('legend')}>{legend}</div>}
      <div className={className('body')}>
        {!hiddenControls && !cardVariant && renderPrev()}
        {renderDates()}
        {!hiddenControls && !cardVariant && renderNext()}
      </div>
      {footerMessages && (
        <div className={className('footer')}>
          {footerMessages.map((message, index) => (
            <CustomMessage key={index}>{message}</CustomMessage>
          ))}
        </div>
      )}
    </div>
  );
};

HorizontalDatePicker.propTypes = {
  startDate: PropTypes.string,
  days: PropTypes.number,
  selectedDate: PropTypes.string,
  footerMessages: PropTypes.array,
  hiddenControls: PropTypes.bool,
  priceTypes: PropTypes.array,
  isTicketsCalendar: PropTypes.bool,
  onDateSelected: PropTypes.func.isRequired,
  isDateUnavailable: PropTypes.func,
  getDateStatus: PropTypes.func,
  onDateSelectedError: PropTypes.func,
  selectOnLoad: PropTypes.bool,
  cardVariant: PropTypes.bool,
  datePricing: PropTypes.object,
  title: PropTypes.string,
  legend: PropTypes.node,
  modalLegendTypes: PropTypes.array,
  modalVenueName: PropTypes.string,
  modalVenueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  modalRidesCards: PropTypes.array,
  modalOpeningHours: PropTypes.object,
  modalMaxDate: PropTypes.instanceOf(Date),
};

HorizontalDatePicker.defaultProps = {
  days: 7,
  hiddenControls: false,
  priceTypes: [],
  isTicketsCalendar: false,
  isDateUnavailable: () => false,
  getDateStatus: () => CALENDAR_DATE_STATUS.AVAILABLE,
  onDateSelectedError: () => {},
  selectOnLoad: false,
  cardVariant: false,
  datePricing: null,
  title: 'SELECT A DATE',
  modalLegendTypes: null,
};

export default HorizontalDatePicker;
